// Data + Auth adapter — Firebase or localStorage fallback.
import { HAS_FIREBASE, ADMIN_EMAIL, auth, db, storage, handleFirestoreError, OperationType } from "./firebase";
import {
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  signOut as fbSignOut, onAuthStateChanged, updateProfile,
  sendEmailVerification, reload, sendPasswordResetEmail,
  GoogleAuthProvider, signInWithPopup,
} from "firebase/auth";
import {
  collection, doc, getDocs, getDoc, addDoc, setDoc, deleteDoc, query,
  where, orderBy, limit, serverTimestamp, onSnapshot,
} from "firebase/firestore";
import { ref as sRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { normalizeProduct } from "./productHelpers";
import {
  uploadFileToDrive,
  authenticateGoogleDrive,
  hasDriveAccessToken,
  getDriveAccessToken,
  getDriveUserEmail,
  disconnectGoogleDrive,
  getDriveFolderConfig,
  setDriveFolderConfig,
  DEFAULT_DRIVE_FOLDER_ID,
  DEFAULT_DRIVE_FOLDER_URL,
} from "./driveStorage";

export {
  uploadFileToDrive,
  authenticateGoogleDrive,
  hasDriveAccessToken,
  getDriveAccessToken,
  getDriveUserEmail,
  disconnectGoogleDrive,
  getDriveFolderConfig,
  setDriveFolderConfig,
  DEFAULT_DRIVE_FOLDER_ID,
  DEFAULT_DRIVE_FOLDER_URL,
};

const uid = () => Math.random().toString(36).slice(2, 10) + Date.now().toString(36);
const nowIso = () => new Date().toISOString();

const LS = { users: "lc_users", session: "lc_session", products: "lc_products", orders: "lc_orders", testimonials: "lc_testimonials" };
const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));

// ---------- SEED ----------
const SEED_VERSION = "v2";
function seed() {
  const currentVersion = localStorage.getItem("lc_seed_version");
  const shouldReseed = currentVersion !== SEED_VERSION;
  if (shouldReseed || !read(LS.products, null)) {
    localStorage.setItem("lc_seed_version", SEED_VERSION);
    const mk = (name, description, price, category, variants) => ({
      id: uid(), name, description, price, category, variants, createdAt: nowIso(),
    });
    write(LS.products, [
     
    ]);
  }
  if (!read(LS.testimonials, null)) {
    write(LS.testimonials, [
      { id: uid(), name: "Ananya Sen", city: "Kolkata", rating: 5, message: "The leather softens with every use — it feels like a bag that tells a story. Beautifully made." },
      { id: uid(), name: "Vikram Iyer", city: "Bengaluru", rating: 5, message: "Ordered via WhatsApp — smooth, personal, and the tote is stunning. Would recommend to anyone." },
      { id: uid(), name: "Meera Choudhury", city: "Delhi", rating: 5, message: "You can feel the hands that made it. Lineacraft is now my only gifting choice." },
    ]);
  }
  if (!read(LS.users, null)) write(LS.users, []);
  if (!read(LS.orders, null)) write(LS.orders, []);
}
if (typeof window !== "undefined") seed();

// ---------- AUTH ----------
export function isAdminEmail(email) {
  if (!email) return false;
  const target = String(email).trim().toLowerCase();
  const envAdmin = (process.env.REACT_APP_ADMIN_EMAIL || "").toLowerCase().trim();
  const allowed = new Set([
    "scorpion004ss@gmail.com",
    "shuvo100.ss@gmail.com",
    ...envAdmin.split(",").map((e) => e.trim().toLowerCase()).filter(Boolean),
  ]);
  return allowed.has(target);
}

function toClientUser(u) {
  if (!u) return null;
  return {
    uid: u.uid, email: u.email, displayName: u.displayName || "",
    emailVerified: u.emailVerified !== false, // localStorage users are considered verified
  };
}

export function onAuth(cb) {
  if (HAS_FIREBASE) {
    return onAuthStateChanged(auth, (u) => cb(u ? toClientUser(u) : null));
  }
  const emit = () => cb(read(LS.session, null));
  emit();
  const h = (e) => { if (e.key === LS.session) emit(); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function signUp({ email, password, name }) {
  if (HAS_FIREBASE) {
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      if (name) await updateProfile(cred.user, { displayName: name });
      // Send verification email (admin skips)
      if (!isAdminEmail(email)) {
        try { await sendEmailVerification(cred.user); } catch (e) { /* ignore */ }
      }
      return { ...toClientUser(cred.user), displayName: name, _needsVerification: !isAdminEmail(email) && !cred.user.emailVerified };
    } catch (err) {
      if (err.code === "auth/operation-not-allowed" || err.message?.includes("PASSWORD_LOGIN_DISABLED")) {
        throw new Error("Email/Password accounts are disabled in your Firebase project. Please use 'Sign in with Google' or enable Email/Password under Firebase Console > Authentication > Sign-in method.");
      }
      throw err;
    }
  }
  const users = read(LS.users, []);
  if (users.find((u) => u.email === email)) throw new Error("Email already registered");
  const user = { uid: uid(), email, password, displayName: name || "", emailVerified: true };
  users.push(user); write(LS.users, users);
  const session = { uid: user.uid, email: user.email, displayName: user.displayName, emailVerified: true };
  write(LS.session, session);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.session }));
  return session;
}

export async function signIn({ email, password }) {
  if (HAS_FIREBASE) {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      // Reload to get latest verification status
      try { await reload(cred.user); } catch (e) { /* ignore */ }
      const u = toClientUser(cred.user);
      if (!u.emailVerified && !isAdminEmail(email)) {
        // Do NOT sign out — we need the user object to resend. Consumer must handle.
        return { ...u, _needsVerification: true };
      }
      return u;
    } catch (err) {
      if (err.code === "auth/operation-not-allowed" || err.message?.includes("PASSWORD_LOGIN_DISABLED")) {
        throw new Error("Email/Password sign-in is disabled in your Firebase project. Please click 'Sign in with Google' or enable Email/Password in Firebase Console (Authentication > Sign-in method).");
      }
      throw err;
    }
  }
  const users = read(LS.users, []);
  const u = users.find((x) => x.email === email && x.password === password);
  if (!u) {
    // Auto-register admin in demo mode
    if (email.toLowerCase() === ADMIN_EMAIL) {
      const newAdmin = { uid: uid(), email, password, displayName: "Admin", emailVerified: true };
      users.push(newAdmin); write(LS.users, users);
      const session = { uid: newAdmin.uid, email, displayName: "Admin", emailVerified: true };
      write(LS.session, session);
      window.dispatchEvent(new StorageEvent("storage", { key: LS.session }));
      return session;
    }
    throw new Error("Invalid email or password");
  }
  const session = { uid: u.uid, email: u.email, displayName: u.displayName, emailVerified: true };
  write(LS.session, session);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.session }));
  return session;
}

export async function signInWithGoogle() {
  if (HAS_FIREBASE) {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: "select_account" });
    const cred = await signInWithPopup(auth, provider);
    const u = toClientUser(cred.user);
    return u;
  }
  const session = { uid: "demo-google-" + Date.now(), email: "shuvo100.ss@gmail.com", displayName: "Admin", emailVerified: true };
  write(LS.session, session);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.session }));
  return session;
}

export async function signOut() {
  if (HAS_FIREBASE) return fbSignOut(auth);
  localStorage.removeItem(LS.session);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.session }));
}

export async function resendVerification() {
  if (!HAS_FIREBASE) return;
  if (auth.currentUser) await sendEmailVerification(auth.currentUser);
}

export async function refreshVerification() {
  if (!HAS_FIREBASE || !auth.currentUser) return false;
  await reload(auth.currentUser);
  return auth.currentUser.emailVerified;
}

export async function resetPassword(email) {
  if (!email || !email.trim()) throw new Error("Email address is required");
  const cleanEmail = email.trim();
  if (HAS_FIREBASE) {
    await sendPasswordResetEmail(auth, cleanEmail);
    return;
  }
  const users = read(LS.users, []);
  const found = users.find((u) => u.email.toLowerCase() === cleanEmail.toLowerCase());
  if (!found) throw new Error("No account found with this email");
  return true;
}

// ---------- PRODUCTS ----------
let cachedProducts = null;
let productsLastFetched = 0;
const CACHE_TTL_MS = 3 * 60 * 1000; // 3 minutes cache
let isProductsSubscribed = false;

export async function listProducts(forceRefresh = false) {
  const isFresh = cachedProducts && (Date.now() - productsLastFetched < CACHE_TTL_MS);
  if ((isProductsSubscribed || isFresh) && !forceRefresh && cachedProducts) {
    return cachedProducts;
  }
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(query(collection(db, "products"), orderBy("createdAt", "desc")));
      const items = snap.docs.map((d) => normalizeProduct({ id: d.id, ...d.data() }));
      cachedProducts = items;
      productsLastFetched = Date.now();
      write(LS.products, items);
      return cachedProducts;
    } catch (e) {
      console.warn("listProducts Firestore error, falling back to local storage:", e);
    }
  }
  cachedProducts = read(LS.products, []).map(normalizeProduct);
  return cachedProducts;
}

export function subscribeProducts(cb) {
  if (HAS_FIREBASE) {
    try {
      isProductsSubscribed = true;
      return onSnapshot(
        query(collection(db, "products"), orderBy("createdAt", "desc")),
        (snap) => {
          const items = snap.docs.map((d) => normalizeProduct({ id: d.id, ...d.data() }));
          cachedProducts = items;
          productsLastFetched = Date.now();
          write(LS.products, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeProducts Firestore error, falling back to local storage:", err);
          cb(read(LS.products, []).map(normalizeProduct));
        }
      );
    } catch (err) {
      console.warn("subscribeProducts setup error:", err);
    }
  }
  cb(read(LS.products, []).map(normalizeProduct));
  const h = (e) => { if (e.key === LS.products) cb(read(LS.products, []).map(normalizeProduct)); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function getProduct(id) {
  if (!id) return null;
  // 1. Check in-memory cache first (0 reads)
  if (cachedProducts && cachedProducts.length > 0) {
    const found = cachedProducts.find((p) => p.id === id);
    if (found) return found;
  }
  // 2. Check localStorage (0 reads)
  const localList = read(LS.products, []).map(normalizeProduct);
  const localFound = localList.find((p) => p.id === id);
  if (localFound) return localFound;

  // 3. Fallback to targeted single document read
  if (HAS_FIREBASE) {
    try {
      const snap = await getDoc(doc(db, "products", id));
      return snap.exists() ? normalizeProduct({ id: snap.id, ...snap.data() }) : null;
    } catch (e) {
      console.warn("getProduct Firestore error, falling back to local storage:", e);
    }
  }
  return null;
}

export async function saveProduct(p) {
  const clean = normalizeProduct(p);
  if (HAS_FIREBASE) {
    try {
      let saved;
      if (clean.id) {
        await setDoc(doc(db, "products", clean.id), { ...clean, updatedAt: serverTimestamp() }, { merge: true });
        saved = clean;
      } else {
        const ref = await addDoc(collection(db, "products"), { ...clean, createdAt: serverTimestamp() });
        saved = { ...clean, id: ref.id };
      }
      if (cachedProducts) {
        const idx = cachedProducts.findIndex((x) => x.id === saved.id);
        if (idx >= 0) cachedProducts[idx] = saved;
        else cachedProducts.unshift(saved);
      }
      return saved;
    } catch (e) {
      console.warn("saveProduct Firestore error, saving to local storage:", e);
    }
  }
  const list = read(LS.products, []);
  if (clean.id) {
    const idx = list.findIndex((x) => x.id === clean.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...clean };
    else list.unshift(clean);
  } else {
    clean.id = uid(); clean.createdAt = nowIso(); list.unshift(clean);
  }
  write(LS.products, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.products }));
  if (cachedProducts) {
    const idx = cachedProducts.findIndex((x) => x.id === clean.id);
    if (idx >= 0) cachedProducts[idx] = clean;
    else cachedProducts.unshift(clean);
  }
  return clean;
}

export async function deleteProduct(id) {
  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "products", id));
    } catch (e) {
      console.warn("deleteProduct Firestore error, deleting from local storage:", e);
    }
  }
  const list = read(LS.products, []).filter((p) => p.id !== id);
  write(LS.products, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.products }));
  if (cachedProducts) {
    cachedProducts = cachedProducts.filter((p) => p.id !== id);
  }
}

// ---------- IMAGE UPLOAD ----------
export async function uploadProductImage(file) {
  // Directly upload product image to the specified Google Drive folder
  try {
    return await uploadFileToDrive(file);
  } catch (driveErr) {
    console.error("Google Drive upload error:", driveErr);
    throw driveErr;
  }
}

// ---------- ORDERS ----------
function decrementVariantStock(products, orderItems) {
  for (const it of orderItems) {
    const p = products.find((x) => x.id === it.id);
    if (!p) continue;
    // Find matching variant, else default
    const v = (p.variants || []).find((v) => v.tag === it.variantTag) || p.variants?.[0];
    if (v) v.stock = Math.max(0, Number(v.stock || 0) - it.quantity);
    // Recompute aggregate stock so isSoldOut works everywhere
    p.stock = (p.variants || []).reduce((s, v) => s + Number(v.stock || 0), 0);
  }
}

export async function createOrder(order) {
  const payload = {
    orderMode: "Online",
    courierName: order.courierName || "Others",
    ...order,
    status: order.status || "New",
    createdAt: nowIso(),
  };
  if (HAS_FIREBASE) {
    try {
      const ref = await addDoc(collection(db, "orders"), { ...payload, createdAt: serverTimestamp() });
      // Decrement stock per-variant
      for (const item of order.items) {
        try {
          const pRef = doc(db, "products", item.id);
          const snap = await getDoc(pRef);
          if (snap.exists()) {
            const p = normalizeProduct({ id: snap.id, ...snap.data() });
            const variants = p.variants.map((v) => v.tag === item.variantTag ? { ...v, stock: Math.max(0, Number(v.stock || 0) - item.quantity) } : v);
            const stock = variants.reduce((s, v) => s + Number(v.stock || 0), 0);
            await setDoc(pRef, { variants, stock }, { merge: true });
          }
        } catch (err) {
          console.warn("Failed to update stock in Firestore for item:", item.id, err);
        }
      }
      const saved = { ...payload, id: ref.id };
      return saved;
    } catch (e) {
      console.warn("createOrder Firestore error, saving to local storage:", e);
    }
  }
  const list = read(LS.orders, []);
  payload.id = uid();
  list.unshift(payload);
  write(LS.orders, list);
  const products = read(LS.products, []);
  decrementVariantStock(products, order.items);
  write(LS.products, products);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.orders }));
  window.dispatchEvent(new StorageEvent("storage", { key: LS.products }));
  return payload;
}

// ---------- ORDERS ----------
let cachedOrders = null;
let ordersLastFetched = 0;
let isOrdersSubscribed = false;

export async function getOrder(id) {
  if (!id) return null;
  const cleanId = String(id).trim();
  const q = cleanId.toLowerCase();

  // 1. Check in-memory cache (0 reads)
  if (cachedOrders && cachedOrders.length > 0) {
    const memFound = cachedOrders.find((o) =>
      o.id === cleanId ||
      o.id.toLowerCase() === q ||
      (o.consignmentNumber && String(o.consignmentNumber).toLowerCase() === q) ||
      (o.trackingNumber && String(o.trackingNumber).toLowerCase() === q)
    );
    if (memFound) return memFound;
  }

  // 2. Check localStorage (0 reads)
  const list = read(LS.orders, []);
  const localFound = list.find((o) =>
    o.id === cleanId ||
    o.id.toLowerCase() === q ||
    (o.consignmentNumber && String(o.consignmentNumber).toLowerCase() === q) ||
    (o.trackingNumber && String(o.trackingNumber).toLowerCase() === q)
  );
  if (localFound) return localFound;

  // 3. Targeted single document lookup
  if (HAS_FIREBASE) {
    try {
      const snap = await getDoc(doc(db, "orders", cleanId));
      if (snap.exists()) {
        const item = { id: snap.id, ...snap.data() };
        if (cachedOrders) {
          const idx = cachedOrders.findIndex((o) => o.id === item.id);
          if (idx >= 0) cachedOrders[idx] = item;
          else cachedOrders.unshift(item);
        }
        return item;
      }
    } catch (e) {
      console.warn("getOrder by ID failed, falling back to query search", e);
    }
    try {
      const qSnap = await getDocs(query(collection(db, "orders"), where("consignmentNumber", "==", cleanId), limit(1)));
      if (!qSnap.empty) {
        const d = qSnap.docs[0];
        const item = { id: d.id, ...d.data() };
        return item;
      }
    } catch (e) {
      console.warn("getOrder by consignmentNumber failed", e);
    }
    try {
      const qSnap2 = await getDocs(query(collection(db, "orders"), where("trackingNumber", "==", cleanId), limit(1)));
      if (!qSnap2.empty) {
        const d = qSnap2.docs[0];
        const item = { id: d.id, ...d.data() };
        return item;
      }
    } catch (e) {
      console.warn("getOrder by trackingNumber failed", e);
    }
  }

  return null;
}

export async function listOrders(forceRefresh = false) {
  const isFresh = cachedOrders && (Date.now() - ordersLastFetched < CACHE_TTL_MS);
  if ((isOrdersSubscribed || isFresh) && !forceRefresh && cachedOrders) {
    return cachedOrders;
  }
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "orders"));
      const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      items.sort((a, b) => String(b.createdAt || b.date || "").localeCompare(String(a.createdAt || a.date || "")));
      cachedOrders = items;
      ordersLastFetched = Date.now();
      write(LS.orders, items);
      return items;
    } catch (e) {
      console.warn("listOrders Firestore error, using local storage:", e);
    }
  }
  cachedOrders = read(LS.orders, []);
  return cachedOrders;
}

export function subscribeOrders(cb) {
  if (HAS_FIREBASE) {
    try {
      isOrdersSubscribed = true;
      return onSnapshot(
        collection(db, "orders"),
        (snap) => {
          const items = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
          items.sort((a, b) => String(b.createdAt || b.date || "").localeCompare(String(a.createdAt || a.date || "")));
          cachedOrders = items;
          ordersLastFetched = Date.now();
          write(LS.orders, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeOrders error:", err);
          cb(read(LS.orders, []));
        }
      );
    } catch (e) {
      console.warn("subscribeOrders setup error:", e);
    }
  }
  cb(read(LS.orders, []));
  const h = (e) => { if (e.key === LS.orders) cb(read(LS.orders, [])); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function listMyOrders(userId) {
  if (cachedOrders && isOrdersSubscribed) {
    return cachedOrders.filter((o) => o.userId === userId);
  }
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(query(collection(db, "orders"), where("userId", "==", userId)));
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (e) {
      console.warn("listMyOrders Firestore error, using local storage:", e);
    }
  }
  return read(LS.orders, []).filter((o) => o.userId === userId);
}

export async function updateOrder(id, patch) {
  if (HAS_FIREBASE) {
    try {
      await setDoc(doc(db, "orders", id), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
    } catch (e) {
      console.warn("updateOrder Firestore error, updating local storage:", e);
    }
  }
  const list = read(LS.orders, []);
  const idx = list.findIndex((o) => o.id === id);
  if (idx >= 0) list[idx] = { ...list[idx], ...patch };
  write(LS.orders, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.orders }));
  if (cachedOrders) {
    const cIdx = cachedOrders.findIndex((o) => o.id === id);
    if (cIdx >= 0) cachedOrders[cIdx] = { ...cachedOrders[cIdx], ...patch };
  }
}

export async function deleteOrder(id) {
  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "orders", id));
    } catch (e) {
      console.warn("deleteOrder Firestore error, updating local storage:", e);
    }
  }
  const list = read(LS.orders, []).filter((o) => o.id !== id);
  write(LS.orders, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS.orders }));
  if (cachedOrders) {
    cachedOrders = cachedOrders.filter((o) => o.id !== id);
  }
}

// ---------- EXPENSES & FINANCIAL MANAGEMENT ----------
const LS_EXPENSES = "lc_expenses";
const LS_INVESTMENTS = "lc_investments";
const LS_WITHDRAWALS = "lc_withdrawals";

let cachedExpenses = null;
let cachedInvestments = null;
let cachedWithdrawals = null;

export async function listExpenses(forceRefresh = false) {
  if (cachedExpenses && !forceRefresh) return cachedExpenses;
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "expenses"));
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        amount: Number(d.data().amount || 0),
      }));
      items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
      cachedExpenses = items;
      write(LS_EXPENSES, items);
      return cachedExpenses;
    } catch (e) {
      console.warn("Failed to load expenses from Firebase, falling back to local storage:", e);
    }
  }
  cachedExpenses = read(LS_EXPENSES, []);
  return cachedExpenses;
}

export function subscribeExpenses(cb) {
  if (HAS_FIREBASE) {
    try {
      return onSnapshot(
        collection(db, "expenses"),
        (snap) => {
          const items = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            amount: Number(d.data().amount || 0),
          }));
          items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
          cachedExpenses = items;
          write(LS_EXPENSES, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeExpenses error:", err);
          cb(read(LS_EXPENSES, []));
        }
      );
    } catch (e) {
      console.warn("subscribeExpenses setup error:", e);
    }
  }
  cb(read(LS_EXPENSES, []));
  const h = (e) => { if (e.key === LS_EXPENSES) cb(read(LS_EXPENSES, [])); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

function handleFirebaseError(e, opName) {
  console.warn(`Firebase Firestore ${opName} error (falling back to local storage):`, e);
}

export async function saveExpense(expense) {
  const payload = {
    category: expense.category || "General",
    description: expense.description || expense.title || "Expense",
    vendor: expense.vendor || expense.title || "Vendor",
    amount: Number(expense.amount || 0),
    date: expense.date || new Date().toISOString().slice(0, 10),
    paymentMethod: expense.paymentMethod || "UPI",
    notes: expense.notes || "",
    updatedAt: nowIso(),
  };

  const list = read(LS_EXPENSES, []);
  if (expense.id) {
    const idx = list.findIndex((e) => e.id === expense.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...payload };
    else list.unshift({ id: expense.id, ...payload });
    payload.id = expense.id;
  } else {
    payload.id = uid();
    list.unshift(payload);
  }
  write(LS_EXPENSES, list);
  cachedExpenses = list;
  window.dispatchEvent(new StorageEvent("storage", { key: LS_EXPENSES }));

  if (HAS_FIREBASE) {
    try {
      if (expense.id) {
        await setDoc(doc(db, "expenses", expense.id), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        const ref = await addDoc(collection(db, "expenses"), { ...payload, createdAt: serverTimestamp() });
        payload.id = ref.id;
      }
      return payload;
    } catch (e) {
      handleFirebaseError(e, "saveExpense");
    }
  }

  return payload;
}

export async function deleteExpense(id) {
  const list = read(LS_EXPENSES, []).filter((e) => e.id !== id);
  write(LS_EXPENSES, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_EXPENSES }));
  cachedExpenses = list;

  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "expenses", id));
      return;
    } catch (e) {
      handleFirebaseError(e, "deleteExpense");
    }
  }
}

// ---------- INVESTMENTS & CAPITAL TRACKING ----------
export async function listInvestments(forceRefresh = false) {
  if (cachedInvestments && !forceRefresh) return cachedInvestments;
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "investments"));
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        amount: Number(d.data().amount || 0),
      }));
      items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
      cachedInvestments = items;
      write(LS_INVESTMENTS, items);
      return cachedInvestments;
    } catch (e) {
      console.warn("Failed to load investments from Firebase, falling back to local storage:", e);
    }
  }
  cachedInvestments = read(LS_INVESTMENTS, []);
  return cachedInvestments;
}

export function subscribeInvestments(cb) {
  if (HAS_FIREBASE) {
    try {
      return onSnapshot(
        collection(db, "investments"),
        (snap) => {
          const items = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            amount: Number(d.data().amount || 0),
          }));
          items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
          cachedInvestments = items;
          write(LS_INVESTMENTS, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeInvestments error:", err);
          cb(read(LS_INVESTMENTS, []));
        }
      );
    } catch (e) {
      console.warn("subscribeInvestments setup error:", e);
    }
  }
  cb(read(LS_INVESTMENTS, []));
  const h = (e) => { if (e.key === LS_INVESTMENTS) cb(read(LS_INVESTMENTS, [])); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function saveInvestment(inv) {
  const payload = {
    date: inv.date || new Date().toISOString().slice(0, 10),
    amount: Number(inv.amount || 0),
    investedBy: inv.investedBy || "Shuvasis",
    notes: inv.notes || "",
    updatedAt: nowIso(),
  };

  const list = read(LS_INVESTMENTS, []);
  if (inv.id) {
    const idx = list.findIndex((i) => i.id === inv.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...payload };
    else list.unshift({ id: inv.id, ...payload });
    payload.id = inv.id;
  } else {
    payload.id = uid();
    list.unshift(payload);
  }
  write(LS_INVESTMENTS, list);
  cachedInvestments = list;
  window.dispatchEvent(new StorageEvent("storage", { key: LS_INVESTMENTS }));

  if (HAS_FIREBASE) {
    try {
      if (inv.id) {
        await setDoc(doc(db, "investments", inv.id), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        const ref = await addDoc(collection(db, "investments"), { ...payload, createdAt: serverTimestamp() });
        payload.id = ref.id;
      }
      return payload;
    } catch (e) {
      handleFirebaseError(e, "saveInvestment");
    }
  }

  return payload;
}

export async function deleteInvestment(id) {
  const list = read(LS_INVESTMENTS, []).filter((i) => i.id !== id);
  write(LS_INVESTMENTS, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_INVESTMENTS }));
  cachedInvestments = list;

  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "investments", id));
      return;
    } catch (e) {
      handleFirebaseError(e, "deleteInvestment");
    }
  }
}

// ---------- WITHDRAWALS TRACKING ----------
export async function listWithdrawals(forceRefresh = false) {
  if (cachedWithdrawals && !forceRefresh) return cachedWithdrawals;
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "withdrawals"));
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        amount: Number(d.data().amount || 0),
      }));
      items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
      cachedWithdrawals = items;
      write(LS_WITHDRAWALS, items);
      return cachedWithdrawals;
    } catch (e) {
      console.warn("Failed to load withdrawals from Firebase, falling back to local storage:", e);
    }
  }
  cachedWithdrawals = read(LS_WITHDRAWALS, []);
  return cachedWithdrawals;
}

export function subscribeWithdrawals(cb) {
  if (HAS_FIREBASE) {
    try {
      return onSnapshot(
        collection(db, "withdrawals"),
        (snap) => {
          const items = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            amount: Number(d.data().amount || 0),
          }));
          items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
          cachedWithdrawals = items;
          write(LS_WITHDRAWALS, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeWithdrawals error:", err);
          cb(read(LS_WITHDRAWALS, []));
        }
      );
    } catch (e) {
      console.warn("subscribeWithdrawals setup error:", e);
    }
  }
  cb(read(LS_WITHDRAWALS, []));
  const h = (e) => { if (e.key === LS_WITHDRAWALS) cb(read(LS_WITHDRAWALS, [])); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function saveWithdrawal(wd) {
  const payload = {
    date: wd.date || new Date().toISOString().slice(0, 10),
    amount: Number(wd.amount || 0),
    withdrawnBy: wd.withdrawnBy || "Shuvasis",
    notes: wd.notes || "",
    updatedAt: nowIso(),
  };

  const list = read(LS_WITHDRAWALS, []);
  if (wd.id) {
    const idx = list.findIndex((w) => w.id === wd.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...payload };
    else list.unshift({ id: wd.id, ...payload });
    payload.id = wd.id;
  } else {
    payload.id = uid();
    list.unshift(payload);
  }
  write(LS_WITHDRAWALS, list);
  cachedWithdrawals = list;
  window.dispatchEvent(new StorageEvent("storage", { key: LS_WITHDRAWALS }));

  if (HAS_FIREBASE) {
    try {
      if (wd.id) {
        await setDoc(doc(db, "withdrawals", wd.id), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        const ref = await addDoc(collection(db, "withdrawals"), { ...payload, createdAt: serverTimestamp() });
        payload.id = ref.id;
      }
      return payload;
    } catch (e) {
      handleFirebaseError(e, "saveWithdrawal");
    }
  }

  return payload;
}

export async function deleteWithdrawal(id) {
  const list = read(LS_WITHDRAWALS, []).filter((w) => w.id !== id);
  write(LS_WITHDRAWALS, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_WITHDRAWALS }));
  cachedWithdrawals = list;

  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "withdrawals", id));
      return;
    } catch (e) {
      handleFirebaseError(e, "deleteWithdrawal");
    }
  }
}

// ---------- TESTIMONIALS ----------

export async function listTestimonials() {
  return [
    {
      id: "1",
      name: "Rahul Sharma",
      city: "Kolkata",
      rating: 5,
      message: "Excellent quality and beautiful craftsmanship.",
      image: "/images/testimonials/fb1.jpeg",
    },
    {
      id: "2",
      name: "Priya Das",
      city: "Bangalore",
      rating: 5,
      message: "The leather bag is premium and exactly as shown.",
      image: "/images/testimonials/fb2.jpeg",
    },
    {
      id: "3",
      name: "Ananya Roy",
      city: "Delhi",
      rating: 5,
      message: "Fast delivery and amazing customer support.",
      image: "/images/testimonials/fb3.jpeg",
    },
    {
      id: "4",
      name: "Ananya Roy",
      city: "Delhi",
      rating: 5,
      message: "Fast delivery and amazing customer support.",
      image: "/images/testimonials/fb4.jpeg",
    },
    {
      id: "5",
      name: "Ananya Roy",
      city: "Delhi",
      rating: 5,
      message: "Fast delivery and amazing customer support.",
      image: "/images/testimonials/fb5.jpeg",
    },
  ];
}

// ---------- OPENING BALANCES / INITIAL HISTORICAL FIGURES ----------
const LS_OPENING_BALANCES = "lineacraft_opening_balances";

export const DEFAULT_OPENING_BALANCES = {
  periodLabel: "Opening Balances / Initial Data",
  openingRevenue: 0,
  openingOrdersCount: 0,
  openingQuantitySold: 0,
  openingCOGS: 0, // purchase expense
  openingShipping: 0, // shipping expense
  openingExpenses: 0, // total operational expenses
  openingLosses: 0, // total loss
  openingProfit: 0, // total profit
  openingCapital: 0, // total overall investment
  openingCapitalShuvasis: 0,
  openingCapitalSourav: 0,
  openingWithdrawals: 0,
  openingWithdrawalsShuvasis: 0,
  openingWithdrawalsSourav: 0,
  openingCashOnHand: 0,
  notes: "",
  updatedAt: new Date().toISOString(),
};

export function normalizeOpeningBalances(data = {}) {
  const num = (v) => {
    if (v === null || v === undefined || v === "") return 0;
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };
  return {
    periodLabel: data.periodLabel || DEFAULT_OPENING_BALANCES.periodLabel,
    openingRevenue: num(data.openingRevenue),
    openingOrdersCount: num(data.openingOrdersCount),
    openingQuantitySold: num(data.openingQuantitySold),
    openingCOGS: num(data.openingCOGS),
    openingShipping: num(data.openingShipping),
    openingExpenses: num(data.openingExpenses),
    openingLosses: num(data.openingLosses),
    openingProfit: num(data.openingProfit),
    openingCapital: num(data.openingCapital),
    openingCapitalShuvasis: num(data.openingCapitalShuvasis),
    openingCapitalSourav: num(data.openingCapitalSourav),
    openingWithdrawals: num(data.openingWithdrawals),
    openingWithdrawalsShuvasis: num(data.openingWithdrawalsShuvasis),
    openingWithdrawalsSourav: num(data.openingWithdrawalsSourav),
    openingCashOnHand: num(data.openingCashOnHand),
    notes: data.notes || "",
    updatedAt: data.updatedAt || new Date().toISOString(),
  };
}

let cachedOpeningBalances = null;

export async function getOpeningBalances(forceRefresh = false) {
  if (cachedOpeningBalances && !forceRefresh) return cachedOpeningBalances;
  const localData = read(LS_OPENING_BALANCES, {});
  if (HAS_FIREBASE) {
    try {
      const snap = await getDoc(doc(db, "settings", "openingBalances"));
      if (snap.exists()) {
        const firestoreData = snap.data();
        const merged = normalizeOpeningBalances({
          ...DEFAULT_OPENING_BALANCES,
          ...localData,
          ...firestoreData,
        });
        cachedOpeningBalances = merged;
        write(LS_OPENING_BALANCES, merged);
        return cachedOpeningBalances;
      } else {
        const initial = normalizeOpeningBalances({ ...DEFAULT_OPENING_BALANCES, ...localData });
        await setDoc(doc(db, "settings", "openingBalances"), initial).catch(() => {});
        cachedOpeningBalances = initial;
        write(LS_OPENING_BALANCES, initial);
        return cachedOpeningBalances;
      }
    } catch (e) {
      console.warn("getOpeningBalances Firestore error, using local storage:", e);
    }
  }
  cachedOpeningBalances = normalizeOpeningBalances({ ...DEFAULT_OPENING_BALANCES, ...localData });
  return cachedOpeningBalances;
}

export function subscribeOpeningBalances(cb) {
  if (HAS_FIREBASE) {
    try {
      return onSnapshot(
        doc(db, "settings", "openingBalances"),
        async (snap) => {
          const localData = read(LS_OPENING_BALANCES, {});
          if (snap.exists()) {
            const merged = normalizeOpeningBalances({
              ...DEFAULT_OPENING_BALANCES,
              ...localData,
              ...snap.data(),
            });
            cachedOpeningBalances = merged;
            write(LS_OPENING_BALANCES, merged);
            cb(merged);
          } else {
            const initial = normalizeOpeningBalances({ ...DEFAULT_OPENING_BALANCES, ...localData });
            await setDoc(doc(db, "settings", "openingBalances"), initial).catch(() => {});
            cachedOpeningBalances = initial;
            write(LS_OPENING_BALANCES, initial);
            cb(initial);
          }
        },
        (err) => {
          console.warn("subscribeOpeningBalances error, fallback to local storage:", err);
          const localData = read(LS_OPENING_BALANCES, {});
          cb(normalizeOpeningBalances({ ...DEFAULT_OPENING_BALANCES, ...localData }));
        }
      );
    } catch (e) {
      console.warn("subscribeOpeningBalances setup error:", e);
    }
  }
  const localData = read(LS_OPENING_BALANCES, {});
  cb(normalizeOpeningBalances({ ...DEFAULT_OPENING_BALANCES, ...localData }));
  const h = (e) => {
    if (e.key === LS_OPENING_BALANCES) {
      cb(normalizeOpeningBalances({ ...DEFAULT_OPENING_BALANCES, ...read(LS_OPENING_BALANCES, {}) }));
    }
  };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function saveOpeningBalances(data) {
  const payload = normalizeOpeningBalances({
    ...DEFAULT_OPENING_BALANCES,
    ...data,
    updatedAt: new Date().toISOString(),
  });
  write(LS_OPENING_BALANCES, payload);
  cachedOpeningBalances = payload;
  window.dispatchEvent(new StorageEvent("storage", { key: LS_OPENING_BALANCES }));

  if (HAS_FIREBASE) {
    try {
      await setDoc(doc(db, "settings", "openingBalances"), payload, { merge: true });
      return payload;
    } catch (e) {
      handleFirebaseError(e, "saveOpeningBalances");
    }
  }
  return payload;
}

// ---------- LOSSES / RETURNS / DAMAGE TRACKING ----------
const LS_LOSSES = "lineacraft_losses";
let cachedLosses = null;

export async function listLosses(forceRefresh = false) {
  if (cachedLosses && !forceRefresh) return cachedLosses;
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "losses"));
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
        amount: Number(d.data().amount || 0),
      }));
      items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
      cachedLosses = items;
      write(LS_LOSSES, items);
      return cachedLosses;
    } catch (e) {
      console.warn("listLosses Firestore error, fallback to local storage:", e);
    }
  }
  cachedLosses = read(LS_LOSSES, []);
  return cachedLosses;
}

export function subscribeLosses(cb) {
  if (HAS_FIREBASE) {
    try {
      return onSnapshot(
        collection(db, "losses"),
        (snap) => {
          const items = snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
            amount: Number(d.data().amount || 0),
          }));
          items.sort((a, b) => String(b.date || b.createdAt || "").localeCompare(String(a.date || a.createdAt || "")));
          cachedLosses = items;
          write(LS_LOSSES, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeLosses error:", err);
          cb(read(LS_LOSSES, []));
        }
      );
    } catch (e) {
      console.warn("subscribeLosses setup error:", e);
    }
  }
  cb(read(LS_LOSSES, []));
  const h = (e) => { if (e.key === LS_LOSSES) cb(read(LS_LOSSES, [])); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function saveLoss(loss) {
  const payload = {
    title: loss.title || "Damaged/Returned Stock",
    category: loss.category || "Damage",
    amount: Number(loss.amount || 0),
    orderId: loss.orderId || "",
    date: loss.date || nowIso().slice(0, 10),
    notes: loss.notes || "",
    recordedBy: loss.recordedBy || "Admin",
    createdAt: nowIso(),
  };

  const list = read(LS_LOSSES, []);
  if (loss.id) {
    const idx = list.findIndex((l) => l.id === loss.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...payload };
    else list.push({ ...payload, id: loss.id });
    payload.id = loss.id;
  } else {
    payload.id = uid();
    list.unshift(payload);
  }
  write(LS_LOSSES, list);
  cachedLosses = list;
  window.dispatchEvent(new StorageEvent("storage", { key: LS_LOSSES }));

  if (HAS_FIREBASE) {
    try {
      if (loss.id) {
        await setDoc(doc(db, "losses", loss.id), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        const ref = await addDoc(collection(db, "losses"), { ...payload, createdAt: serverTimestamp() });
        payload.id = ref.id;
      }
      return payload;
    } catch (e) {
      handleFirebaseError(e, "saveLoss");
    }
  }

  return payload;
}

export async function deleteLoss(id) {
  const list = read(LS_LOSSES, []).filter((l) => l.id !== id);
  write(LS_LOSSES, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_LOSSES }));
  cachedLosses = list;

  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "losses", id));
      return;
    } catch (e) {
      handleFirebaseError(e, "deleteLoss");
    }
  }
}

// ---------- MARKETPLACE ORDERS / SETTLEMENTS TRACKING ----------
const LS_MARKETPLACE_ORDERS = "lineacraft_marketplace_orders";
let cachedMarketplaceOrders = null;

export async function listMarketplaceOrders(forceRefresh = false) {
  if (cachedMarketplaceOrders && !forceRefresh) return cachedMarketplaceOrders;
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "marketplaceOrders"));
      const items = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          unitsSold: Number(data.unitsSold || 0),
          grossSales: Number(data.grossSales || 0),
          commissionFee: Number(data.commissionFee || 0),
          logisticsFee: Number(data.logisticsFee || 0),
          taxDeductions: Number(data.taxDeductions || 0),
          otherDeductions: Number(data.otherDeductions || 0),
          netPayout: Number(data.netPayout || 0),
          cogs: Number(data.cogs || 0),
          netProfit: Number(data.netProfit || 0),
        };
      });
      items.sort((a, b) => String(b.startDate || b.payoutDate || b.createdAt || "").localeCompare(String(a.startDate || a.payoutDate || a.createdAt || "")));
      cachedMarketplaceOrders = items;
      write(LS_MARKETPLACE_ORDERS, items);
      return cachedMarketplaceOrders;
    } catch (e) {
      console.warn("listMarketplaceOrders Firestore error, fallback to local storage:", e);
    }
  }
  cachedMarketplaceOrders = read(LS_MARKETPLACE_ORDERS, []);
  return cachedMarketplaceOrders;
}

export function subscribeMarketplaceOrders(cb) {
  if (HAS_FIREBASE) {
    try {
      return onSnapshot(
        collection(db, "marketplaceOrders"),
        (snap) => {
          const items = snap.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              ...data,
              unitsSold: Number(data.unitsSold || 0),
              grossSales: Number(data.grossSales || 0),
              commissionFee: Number(data.commissionFee || 0),
              logisticsFee: Number(data.logisticsFee || 0),
              taxDeductions: Number(data.taxDeductions || 0),
              otherDeductions: Number(data.otherDeductions || 0),
              netPayout: Number(data.netPayout || 0),
              cogs: Number(data.cogs || 0),
              netProfit: Number(data.netProfit || 0),
            };
          });
          items.sort((a, b) => String(b.startDate || b.payoutDate || b.createdAt || "").localeCompare(String(a.startDate || a.payoutDate || a.createdAt || "")));
          cachedMarketplaceOrders = items;
          write(LS_MARKETPLACE_ORDERS, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeMarketplaceOrders error:", err);
          cb(read(LS_MARKETPLACE_ORDERS, []));
        }
      );
    } catch (e) {
      console.warn("subscribeMarketplaceOrders setup error:", e);
    }
  }
  cb(read(LS_MARKETPLACE_ORDERS, []));
  const h = (e) => { if (e.key === LS_MARKETPLACE_ORDERS) cb(read(LS_MARKETPLACE_ORDERS, [])); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function saveMarketplaceOrder(mpOrder) {
  const payload = {
    marketplace: mpOrder.marketplace || "Amazon",
    marketplaceCustomName: mpOrder.marketplaceCustomName || "",
    settlementRef: mpOrder.settlementRef || "",
    startDate: mpOrder.startDate || nowIso().slice(0, 10),
    endDate: mpOrder.endDate || nowIso().slice(0, 10),
    payoutDate: mpOrder.payoutDate || nowIso().slice(0, 10),
    status: mpOrder.status || "Settled",
    unitsSold: Number(mpOrder.unitsSold || 0),
    itemsSummary: mpOrder.itemsSummary || "",
    grossSales: Number(mpOrder.grossSales || 0),
    commissionFee: Number(mpOrder.commissionFee || 0),
    logisticsFee: Number(mpOrder.logisticsFee || 0),
    taxDeductions: Number(mpOrder.taxDeductions || 0),
    otherDeductions: Number(mpOrder.otherDeductions || 0),
    netPayout: Number(mpOrder.netPayout || 0),
    cogs: Number(mpOrder.cogs || 0),
    netProfit: Number(mpOrder.netProfit || 0),
    utrNumber: mpOrder.utrNumber || "",
    paymentAccount: mpOrder.paymentAccount || "",
    notes: mpOrder.notes || "",
    updatedAt: nowIso(),
  };

  const list = read(LS_MARKETPLACE_ORDERS, []);
  if (mpOrder.id) {
    const idx = list.findIndex((m) => m.id === mpOrder.id);
    if (idx >= 0) list[idx] = { ...list[idx], ...payload };
    else list.push({ ...payload, id: mpOrder.id });
    payload.id = mpOrder.id;
  } else {
    payload.id = uid();
    payload.createdAt = nowIso();
    list.unshift(payload);
  }
  write(LS_MARKETPLACE_ORDERS, list);
  cachedMarketplaceOrders = list;
  window.dispatchEvent(new StorageEvent("storage", { key: LS_MARKETPLACE_ORDERS }));

  if (HAS_FIREBASE) {
    try {
      if (mpOrder.id) {
        await setDoc(doc(db, "marketplaceOrders", mpOrder.id), { ...payload, updatedAt: serverTimestamp() }, { merge: true });
      } else {
        const ref = await addDoc(collection(db, "marketplaceOrders"), { ...payload, createdAt: serverTimestamp() });
        payload.id = ref.id;
      }
      return payload;
    } catch (e) {
      handleFirebaseError(e, "saveMarketplaceOrder");
    }
  }

  return payload;
}

export async function deleteMarketplaceOrder(id) {
  const list = read(LS_MARKETPLACE_ORDERS, []).filter((m) => m.id !== id);
  write(LS_MARKETPLACE_ORDERS, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_MARKETPLACE_ORDERS }));
  cachedMarketplaceOrders = list;

  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "marketplaceOrders", id));
      return;
    } catch (e) {
      handleFirebaseError(e, "deleteMarketplaceOrder");
    }
  }
}

// // ============================================================================
// MASTER SKU CATALOG & INVENTORY MANAGEMENT
// ============================================================================
const LS_MASTER_SKUS = "lc_master_skus";

let cachedMasterSkus = null;
let masterSkusLastFetched = 0;
let isMasterSkusSubscribed = false;

export function normalizeMasterSku(s) {
  if (!s) return null;
  const sku = (s.sku || s.skuId || s.code || "").trim().toUpperCase();
  const name = (s.name || s.title || "").trim();
  const category = (s.category || "Leather Bags").trim();
  const purchaseCost = Number(s.purchaseCost ?? s.costPrice ?? s.productCost ?? 0);
  const saleCost = Number(s.saleCost ?? s.salePrice ?? s.price ?? 0);
  const mrp = Number(s.mrp != null && s.mrp !== "" ? s.mrp : (saleCost > 0 ? Math.round(saleCost * 1.4) : 0));
  const quantity = Math.max(0, Number(s.quantity ?? s.stock ?? 0));
  const additionalCost = Number(s.additionalCost ?? s.packagingCost ?? s.extraCharge ?? 80);
  const gstRate = Number(s.gstRate ?? 0);
  const shippingCharge = Number(s.shippingCharge ?? 0);
  const description = (s.description || s.details || "").trim();
  const material = (s.material || "").trim();
  const colour = (s.colour || s.color || "").trim();
  const size = (s.size || s.dimensions || "").trim();
  const weight = (s.weight || "").trim();
  const bagType = (s.bagType || "").trim();
  const barcode = (s.barcode || sku).trim();
  const images = Array.isArray(s.images) ? s.images : (s.imageUrl ? [s.imageUrl] : []);

  return {
    id: s.id || uid(),
    sku: sku || `SKU-${Date.now().toString(36).toUpperCase()}`,
    name: name || "Untitled SKU Product",
    category,
    purchaseCost,
    saleCost,
    mrp,
    quantity,
    additionalCost,
    gstRate,
    shippingCharge,
    description,
    material,
    colour,
    size,
    weight,
    bagType,
    barcode,
    images,
    createdAt: s.createdAt || nowIso(),
    updatedAt: s.updatedAt || nowIso(),
  };
}

export async function listMasterSkus(forceRefresh = false) {
  const isFresh = cachedMasterSkus && (Date.now() - masterSkusLastFetched < CACHE_TTL_MS);
  if ((isMasterSkusSubscribed || isFresh) && !forceRefresh && cachedMasterSkus) {
    return cachedMasterSkus;
  }
  if (HAS_FIREBASE) {
    try {
      const snap = await getDocs(collection(db, "masterSkus"));
      let items = snap.docs.map((d) => normalizeMasterSku({ id: d.id, ...d.data() }));
      items.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));

      cachedMasterSkus = items;
      masterSkusLastFetched = Date.now();
      write(LS_MASTER_SKUS, items);
      return items;
    } catch (e) {
      console.warn("listMasterSkus Firestore error, falling back to local storage:", e);
    }
  }

  cachedMasterSkus = read(LS_MASTER_SKUS, []).map(normalizeMasterSku);
  return cachedMasterSkus;
}

export function subscribeMasterSkus(cb) {
  if (HAS_FIREBASE) {
    try {
      isMasterSkusSubscribed = true;
      return onSnapshot(
        collection(db, "masterSkus"),
        (snap) => {
          let items = snap.docs.map((d) => normalizeMasterSku({ id: d.id, ...d.data() }));
          items.sort((a, b) => new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0));
          cachedMasterSkus = items;
          masterSkusLastFetched = Date.now();
          write(LS_MASTER_SKUS, items);
          cb(items);
        },
        (err) => {
          console.warn("subscribeMasterSkus Firestore error, falling back to local storage:", err);
          cb(read(LS_MASTER_SKUS, []).map(normalizeMasterSku));
        }
      );
    } catch (err) {
      console.warn("subscribeMasterSkus setup error:", err);
    }
  }

  cb(read(LS_MASTER_SKUS, []).map(normalizeMasterSku));
  const h = (e) => { if (e.key === LS_MASTER_SKUS) cb(read(LS_MASTER_SKUS, []).map(normalizeMasterSku)); };
  window.addEventListener("storage", h);
  return () => window.removeEventListener("storage", h);
}

export async function getMasterSku(idOrSku) {
  if (!idOrSku) return null;
  const q = String(idOrSku).trim().toLowerCase();

  // 1. Check in-memory cache first (0 reads)
  if (cachedMasterSkus && cachedMasterSkus.length > 0) {
    const match = cachedMasterSkus.find((m) => m.id === idOrSku || (m.sku || "").toLowerCase() === q);
    if (match) return match;
  }

  // 2. Check localStorage (0 reads)
  const list = read(LS_MASTER_SKUS, []).map(normalizeMasterSku);
  const localMatch = list.find((m) => m.id === idOrSku || (m.sku || "").toLowerCase() === q);
  if (localMatch) return localMatch;

  // 3. Targeted Firestore read with limit(1) (1 read max)
  if (HAS_FIREBASE) {
    try {
      const targetRef = doc(db, "masterSkus", idOrSku);
      const snap = await getDoc(targetRef);
      if (snap.exists()) {
        const item = normalizeMasterSku({ id: snap.id, ...snap.data() });
        if (cachedMasterSkus) {
          const idx = cachedMasterSkus.findIndex((m) => m.id === item.id);
          if (idx >= 0) cachedMasterSkus[idx] = item;
          else cachedMasterSkus.unshift(item);
        }
        return item;
      }
      const qSnap = await getDocs(query(collection(db, "masterSkus"), where("sku", "==", idOrSku), limit(1)));
      if (!qSnap.empty) {
        const d = qSnap.docs[0];
        const item = normalizeMasterSku({ id: d.id, ...d.data() });
        if (cachedMasterSkus) {
          const idx = cachedMasterSkus.findIndex((m) => m.id === item.id);
          if (idx >= 0) cachedMasterSkus[idx] = item;
          else cachedMasterSkus.unshift(item);
        }
        return item;
      }
    } catch (e) {
      console.warn("getMasterSku Firestore error, falling back to local storage:", e);
    }
  }
  return null;
}

export async function saveMasterSku(item) {
  const norm = normalizeMasterSku(item);
  norm.updatedAt = nowIso();
  if (!norm.id) {
    norm.id = uid();
    norm.createdAt = nowIso();
  }

  // Persist to local state immediately
  const list = read(LS_MASTER_SKUS, []);
  const existingIdx = list.findIndex((m) => m.id === norm.id || (m.sku && m.sku.toLowerCase() === norm.sku.toLowerCase()));
  if (existingIdx >= 0) {
    list[existingIdx] = { ...list[existingIdx], ...norm };
  } else {
    list.unshift(norm);
  }
  write(LS_MASTER_SKUS, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_MASTER_SKUS }));

  if (cachedMasterSkus) {
    const idx = cachedMasterSkus.findIndex((m) => m.id === norm.id || (m.sku && m.sku.toLowerCase() === norm.sku.toLowerCase()));
    if (idx >= 0) cachedMasterSkus[idx] = { ...cachedMasterSkus[idx], ...norm };
    else cachedMasterSkus.unshift(norm);
  }

  if (HAS_FIREBASE) {
    try {
      const payload = {};
      for (const [k, v] of Object.entries(norm)) {
        if (v !== undefined) payload[k] = v;
      }
      payload.updatedAt = serverTimestamp();

      await setDoc(doc(db, "masterSkus", norm.id), payload, { merge: true });
    } catch (e) {
      console.error("Firebase saveMasterSku error:", e);
      handleFirestoreError(e, OperationType.WRITE, `masterSkus/${norm.id}`);
    }
  }

  return norm;
}

export async function deleteMasterSku(id) {
  const list = read(LS_MASTER_SKUS, []).filter((m) => m.id !== id && m.sku !== id);
  write(LS_MASTER_SKUS, list);
  window.dispatchEvent(new StorageEvent("storage", { key: LS_MASTER_SKUS }));
  if (cachedMasterSkus) {
    cachedMasterSkus = cachedMasterSkus.filter((m) => m.id !== id && m.sku !== id);
  }

  if (HAS_FIREBASE) {
    try {
      await deleteDoc(doc(db, "masterSkus", id));
    } catch (e) {
      console.error("Firebase deleteMasterSku error:", e);
      handleFirestoreError(e, OperationType.DELETE, `masterSkus/${id}`);
    }
  }
}

export async function updateMasterSkuStock(idOrSku, quantityChange, isAbsolute = false) {
  let targetId = idOrSku;

  if (HAS_FIREBASE) {
    try {
      let targetRef = doc(db, "masterSkus", idOrSku);
      let snap = await getDoc(targetRef);
      if (!snap.exists()) {
        const qSnap = await getDocs(query(collection(db, "masterSkus"), where("sku", "==", idOrSku), limit(1)));
        if (!qSnap.empty) {
          const match = qSnap.docs[0];
          targetRef = doc(db, "masterSkus", match.id);
          snap = match;
          targetId = match.id;
        }
      }
      if (snap.exists()) {
        const currentQty = Number(snap.data().quantity || 0);
        const newQty = isAbsolute ? Math.max(0, Number(quantityChange)) : Math.max(0, currentQty + Number(quantityChange));
        await setDoc(targetRef, { quantity: newQty, updatedAt: serverTimestamp() }, { merge: true });
      }
    } catch (e) {
      console.error("Firebase updateMasterSkuStock error:", e);
    }
  }

  const list = read(LS_MASTER_SKUS, []);
  const idx = list.findIndex((m) => m.id === targetId || (m.sku && m.sku.toLowerCase() === String(idOrSku).toLowerCase()));
  if (idx >= 0) {
    const item = list[idx];
    const newQty = isAbsolute ? Math.max(0, Number(quantityChange)) : Math.max(0, Number(item.quantity || 0) + Number(quantityChange));
    item.quantity = newQty;
    item.updatedAt = nowIso();
    list[idx] = item;
    write(LS_MASTER_SKUS, list);
    window.dispatchEvent(new StorageEvent("storage", { key: LS_MASTER_SKUS }));
    if (cachedMasterSkus) {
      const cIdx = cachedMasterSkus.findIndex((m) => m.id === targetId || (m.sku && m.sku.toLowerCase() === String(idOrSku).toLowerCase()));
      if (cIdx >= 0) cachedMasterSkus[cIdx].quantity = newQty;
    }
    return item;
  }
  return null;
}

export async function syncAllMasterSkusToFirebase() {
  if (!HAS_FIREBASE) return { synced: 0, total: 0 };
  const localList = read(LS_MASTER_SKUS, []).map(normalizeMasterSku);
  if (!localList.length) return { synced: 0, total: 0 };

  let synced = 0;
  for (const item of localList) {
    try {
      await setDoc(doc(db, "masterSkus", item.id), { ...item, updatedAt: serverTimestamp() }, { merge: true });
      synced++;
    } catch (err) {
      console.warn("syncAllMasterSkusToFirebase item error:", item.sku, err);
    }
  }
  return { synced, total: localList.length };
}

export async function importProductsToMasterSkus(products = []) {
  if (!Array.isArray(products) || products.length === 0) return { added: 0, updated: 0 };
  const existing = await listMasterSkus(true);
  const existingSkus = new Set(existing.map((s) => s.sku.toLowerCase()));
  let addedCount = 0;
  let updatedCount = 0;

  for (let i = 0; i < products.length; i++) {
    const p = products[i];
    const defaultSku = `LC-${(p.category || "PROD").slice(0, 3).toUpperCase()}-${String(i + 1).padStart(3, "0")}`;
    const skuCode = (p.sku || defaultSku).trim().toUpperCase();

    const skuObj = {
      sku: skuCode,
      name: p.name || `Product #${i + 1}`,
      category: p.category || "Leather Bags",
      purchaseCost: Number(p.productCost || p.costPrice || Math.round(Number(p.price || 0) * 0.45)),
      saleCost: Number(p.price || 0),
      mrp: Number(p.mrp || p.price || 0),
      quantity: Number(p.stock || p.variants?.[0]?.stock || 10),
      additionalCost: Number(p.packagingCost || 80) + Number(p.extraCharge || 0),
      gstRate: Number(p.gstRate || 0),
      shippingCharge: Number(p.shippingCharge || 0),
      description: p.description || "",
      material: p.material || "",
      colour: p.colour || "",
      size: p.size || "",
      weight: p.weight || "",
      bagType: p.bagType || "",
      images: p.images || [],
    };

    if (existingSkus.has(skuCode.toLowerCase())) {
      const match = existing.find((s) => s.sku.toLowerCase() === skuCode.toLowerCase());
      if (match) {
        await saveMasterSku({ ...skuObj, id: match.id });
        updatedCount++;
      }
    } else {
      await saveMasterSku(skuObj);
      existingSkus.add(skuCode.toLowerCase());
      addedCount++;
    }
  }

  return { added: addedCount, updated: updatedCount };
}


